/**
 * Makes the error body of a `responseType: "blob"` request readable (BUG-704).
 *
 * A file download asks axios for a Blob, and axios applies that to EVERY
 * response — including a refusal. So a 404 `{"code": "BILLING_PDF_NOT_READY"}`
 * reaches the caller as a Blob, and `extractApiError` (which reads `code` /
 * `detail` from a plain object) can read neither: the user was shown the
 * generic «Произошла ошибка» while the server had said exactly why.
 *
 * Call this in the `catch` of a blob request. When the error carries a Blob
 * body whose text is JSON, the body is replaced IN PLACE with the parsed object
 * and the same error is returned, so `status`, `headers` and `isAxiosError`
 * are untouched and every downstream reader (`extractApiErrorMessage`,
 * `readApiErrorStatus`) works unchanged. Anything else — an HTML error page,
 * an empty body, an unreadable Blob — leaves the error exactly as it was, and
 * this function never throws (it runs inside error handling, where a second
 * exception would replace a bad message with none at all).
 */
export async function withReadableBlobErrorBody(
  err: unknown,
): Promise<unknown> {
  if (err == null || typeof err !== "object") return err;
  const response = (err as { response?: unknown }).response;
  if (response == null || typeof response !== "object") return err;
  const data = (response as { data?: unknown }).data;
  if (!(data instanceof Blob)) return err;

  try {
    const text = await readBlobText(data);
    const trimmed = text.trim();
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return err;
    (response as { data: unknown }).data = JSON.parse(trimmed) as unknown;
  } catch {
    // Not JSON, or not readable: keep the original error untouched.
  }
  return err;
}

/**
 * Reads a Blob as text. `Blob.text()` everywhere a real browser runs; the
 * `FileReader` path covers runtimes whose Blob predates `.text()` (jsdom, which
 * the test suite runs on, is one of them).
 */
function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === "function") return blob.text();
  return new Promise<string>((resolve, reject) => {
    const reader = new window.FileReader();
    reader.onload = () => {
      resolve(typeof reader.result === "string" ? reader.result : "");
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error("blob read failed"));
    };
    reader.readAsText(blob);
  });
}
